import React, { useState, useEffect } from 'react';
import { useBooking } from '../context/BookingContext';
import { apiService } from '../services/apiService'; // Adjust import path as needed

const Booking = ({ navigateTo }) => {
  const { state, dispatch, formatCurrency, calculateNights } = useBooking();
  
  const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // vansh (this change) - Added new fields to state
    dateOfBirth: '',
    gstNumber: '',
    // vansh -- ends here
    country: '',
    city: '',
    state: '',
    pincode: '',
    specialRequests: '',
    terms: false,
    cancellation: false
  });
  const [promoCode, setPromoCode] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // vansh (this change) - Added form validity state to control button status
  const [isFormValid, setIsFormValid] = useState(false);
  // vansh -- ends here

  const nights = calculateNights();

  // vansh (this change) - Real-time validation effect
  useEffect(() => {
    const isValid = 
      formData.title.trim() !== '' &&
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      // formData.dateOfBirth.trim() !== '' && // Added DOB validation
      formData.email.trim() !== '' &&
      /\S+@\S+\.\S+/.test(formData.email) &&
      formData.phone.trim() !== '' &&
      formData.country.trim() !== '' &&
      // formData.city.trim() !== '' &&
      formData.state.trim() !== '' &&
      // formData.pincode.trim() !== '' &&
      formData.terms === true &&
      formData.cancellation === true;

    setIsFormValid(isValid);
  }, [formData]);
  // vansh -- ends here

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Prepare promo code data for API
  const preparePromoData = (code) => {
    // Prepare room details for promo API
    const roomDetails = state.cart.map((item, index) => ({
      room_number: index + 1,
      category_id: item.categoryId,
      category_name: item.roomName,
      rate_plan_id: item.ratePlanId,
      rate_plan_name: item.rateName,
      adults_count: item.adultsCount || item.guestInfo?.adults || 1,
      children_count: item.childrenCount || item.guestInfo?.children || 0,
      room_price: item.roomPrice,
      offer_applied: item.offerName || '',
      quantity: item.quantity
    }));

    // Calculate total guests
    const totalGuests = roomDetails.reduce((total, room) => {
      return total + (room.adults_count + room.children_count) * room.quantity;
    }, 0);

    const promoData = {
      promo_code: code,
      booking_details: {
        hotel_id: state.searchState.hotelId,
        hotel_name: state.searchState.hotel,
        check_in: state.searchState.checkin,
        check_out: state.searchState.checkout,
        total_nights: nights,
        total_guests: totalGuests,
        total_rooms: state.cart.reduce((total, item) => total + item.quantity, 0)
      },
      room_details: roomDetails,
      subtotal: state.subtotal
    };

    return promoData;
  };

  // Apply promo code using API service
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      alert('Please enter a promo code.');
      return;
    }

    if (state.hasDiscount) {
      alert("Discount already applied.");
      return;
    }

    setIsApplyingPromo(true);
    
    try {
      const promoData = preparePromoData(promoCode);
      const result = await apiService.applyPromoCode(promoData);
      
      if (result.success) {
        // Apply discount based on API response
        const discountAmount = result.discount_amount || result.discountAmount;
        
        dispatch({ 
          type: 'APPLY_DISCOUNT', 
          payload: { 
            discountAmount: discountAmount,
            promoCode: promoCode.toUpperCase()
          } 
        });
        
        alert(`Success: ${formatCurrency(discountAmount)} Discount Applied! Final Total: ${formatCurrency(state.finalTotal)}`);
      } else {
        alert(result.message || 'Invalid promo code. Please try again.');
        setPromoCode('');
      }
    } catch (error) {
      console.error('Failed to apply promo code:', error);
      
      // Fallback to mock validation if API fails
      const mockResult = await validatePromoCodeMock(promoCode, state.cart, state.subtotal);
      if (mockResult.success) {
        dispatch({ 
          type: 'APPLY_DISCOUNT', 
          payload: { 
            discountAmount: mockResult.discountAmount,
            promoCode: promoCode.toUpperCase()
          } 
        });
        alert(`Success: ${formatCurrency(mockResult.discountAmount)} Discount Applied! Final Total: ${formatCurrency(state.finalTotal)}`);
      } else {
        alert(mockResult.message);
        setPromoCode('');
      }
    } finally {
      setIsApplyingPromo(false);
    }
  };

  // Mock API call for promo code validation (fallback)
  const validatePromoCodeMock = async (code, cartItems, subtotal) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (code.toUpperCase() === 'PROMO2025') {
          resolve({
            success: true,
            discountAmount: 4160,
            discountType: 'fixed',
            message: 'Promo code applied successfully!',
            newTotal: subtotal - 4160
          });
        } else if (code.toUpperCase() === 'SAVE10') {
          const discount = subtotal * 0.1;
          resolve({
            success: true,
            discountAmount: Math.round(discount),
            discountType: 'percentage',
            message: '10% discount applied successfully!',
            newTotal: subtotal - discount
          });
        } else {
          resolve({
            success: false,
            message: 'Invalid promo code. Please try again.'
          });
        }
      }, 1000);
    });
  };

  // Prepare booking data for API
  const prepareBookingData = () => {
    const finalTotal = state.hasDiscount ? state.finalTotal : state.total;
    const subtotal = state.subtotal;
    const taxes = state.taxes;
    const discountAmount = state.hasDiscount ? state.discountAmount : 0;
    const promoCodeUsed = state.hasDiscount ? state.promoCode : '';

    // Prepare room details
    const roomDetails = state.cart.map((item, index) => ({
      room_number: index + 1,
      category_id: item.categoryId,
      category_name: item.roomName,
      rate_plan_id: item.ratePlanId,
      rate_plan_name: item.rateName,
      adults_count: item.adultsCount || item.guestInfo?.adults || 1,
      children_count: item.childrenCount || item.guestInfo?.children || 0,
      room_price: item.roomPrice,
      offer_applied: item.offerName || '',
      quantity: item.quantity
    }));

    // Calculate total guests
    const totalGuests = roomDetails.reduce((total, room) => {
      return total + (room.adults_count + room.children_count) * room.quantity;
    }, 0);

    const bookingData = {
      // Guest Information
      guest_info: {
        title: formData.title,
        first_name: formData.firstName,
        last_name: formData.lastName,
        // vansh (this change) - Added new fields to API payload
        date_of_birth: formData.dateOfBirth,
        gst_number: formData.gstNumber,
        // vansh -- ends here
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        special_requests: formData.specialRequests
      },

      // Booking Details
      booking_details: {
        hotel_id: state.searchState.hotelId,
        hotel_name: state.searchState.hotel,
        check_in: state.searchState.checkin,
        check_out: state.searchState.checkout,
        total_nights: nights,
        total_guests: totalGuests,
        total_rooms: state.cart.reduce((total, item) => total + item.quantity, 0)
      },

      // Room Details
      room_details: roomDetails,

      // Pricing Information
      pricing: {
        subtotal: subtotal,
        taxes: taxes,
        tax_percentage: 18,
        discount_amount: discountAmount,
        promo_code: promoCodeUsed,
        final_total: finalTotal,
        currency: 'INR'
      },

      // Additional metadata
      metadata: {
        booking_timestamp: new Date().toISOString()
      }
    };

    return bookingData;
  };

    const handleSubmit = async (e) => {
      if(e) e.preventDefault();
      
      if (isSubmitting || !isFormValid) return;

      setIsSubmitting(true);

      try {
        // Prepare booking data
        const bookingData = prepareBookingData();
        
        console.log('Submitting booking data:', bookingData);

        // Submit to API using ApiService
        const apiResult = await apiService.confirmBooking(bookingData);

        // Check if API call was successful
        if (apiResult.success) {
          // Store booking reference and reservation ID for future reference
          const bookingReference = apiResult.data?.booking_reference || apiResult.booking_reference || 'N/A';
          const reservationId = apiResult.data?.reservation_id || apiResult.reservation_id || 'N/A';
          
          // Check if there's a payment redirect URL
          const paymentRedirectUrl = apiResult.data?.payment_redirect_url || apiResult.payment_redirect_url;
          
          if (paymentRedirectUrl) {
            // Store booking info in sessionStorage before redirecting
            const bookingInfo = {
              bookingReference,
              reservationId,
              hotelName: state.searchState.hotel,
              checkin: state.searchState.checkin,
              checkout: state.searchState.checkout,
              guestName: `${formData.firstName} ${formData.lastName}`,
              guestEmail: formData.email,
              finalTotal: state.hasDiscount ? state.finalTotal : state.total,
              timestamp: new Date().toISOString(),
              paymentStatus: 'pending'
            };
            sessionStorage.setItem('bookingInfo', JSON.stringify(bookingInfo));
            
            // Clear cart from context/session
            dispatch({ type: 'CLEAR_CART' });
            sessionStorage.removeItem('bookingSession');
            
            // Redirect to payment gateway silently (no alert)
            window.location.href = paymentRedirectUrl;
            
            // Return early since we're redirecting
            return;
          } else {
            // If no payment redirect URL, handle as completed booking
            const bookingInfo = {
              bookingReference,
              reservationId,
              hotelName: state.searchState.hotel,
              checkin: state.searchState.checkin,
              checkout: state.searchState.checkout,
              guestName: `${formData.firstName} ${formData.lastName}`,
              guestEmail: formData.email,
              finalTotal: state.hasDiscount ? state.finalTotal : state.total,
              timestamp: new Date().toISOString(),
              paymentStatus: 'completed'
            };
            sessionStorage.setItem('bookingInfo', JSON.stringify(bookingInfo));
            
            // Clear session and redirect to confirmation page
            sessionStorage.removeItem('bookingSession');
            dispatch({ type: 'CLEAR_CART' });
            navigateTo('confirmation'); // Redirect to a confirmation page
          }
        } else {
          // Handle API error response
          alert(`Booking failed: ${apiResult.message || 'Unknown error occurred. Please try again.'}`);
        }

      } catch (error) {
        console.error('Booking submission error:', error);
        alert('Failed to process booking. Please check your connection and try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

  // ... rest of your component JSX remains the same
  if (!state.cart || state.cart.length === 0) {
    return (
      <div className="container mx-auto py-8 px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text-primary mb-4">No Booking Found</h2>
          <p className="text-text-secondary mb-6">Please select rooms before proceeding to checkout.</p>
          <button
            onClick={() => navigateTo('index')}
            className="bg-brand-accent text-text-dark-on-accent px-6 py-2 rounded-md font-semibold hover:bg-brand-accent-hover transition-colors"
          >
            Back to Room Selection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 lg:py-8 px-4 lg:px-8 pb-32 lg:pb-8">
    
      <div className="flex flex-col lg:flex-row lg:gap-8">
        
        {/* Main Content */}
        <div className="lg:w-2/3 w-full space-y-4 lg:space-y-6">
          {/* Back Button */}
          <button
            onClick={() => navigateTo('index')}
            className="flex items-center text-sm font-medium text-text-secondary hover:text-text-primary py-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4 mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Back to Selection
          </button>

          {/* vansh (this change) - Changed "Order Summary" to "Booking Summary" and swapped icon to Clipboard/Checklist */}
          <div className="lg:hidden bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
            <button 
              onClick={() => setIsMobileSummaryOpen(!isMobileSummaryOpen)}
              className="w-full flex justify-between items-center p-4 bg-gray-50 text-left"
            >
               <div className="flex items-center gap-2">
                 {/* New Icon: Clipboard/List instead of Shopping Bag */}
                 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-brand-accent">
                   <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                 </svg>
                 <span className="font-semibold text-text-primary text-sm">Booking Summary</span>
               </div>
               <div className="flex items-center gap-2">
                 <span className="font-bold text-text-dark-on-accent">
                   {formatCurrency(state.hasDiscount ? state.finalTotal : state.total)}
                 </span>
                 <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={2} 
                    stroke="currentColor" 
                    className={`w-4 h-4 text-gray-500 transition-transform ${isMobileSummaryOpen ? 'rotate-180' : ''}`}
                  >
                   <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                 </svg>
               </div>
            </button>
            {isMobileSummaryOpen && (
              <div className="p-4 border-t border-gray-100 bg-white">
                <BookingSummary />
              </div>
            )}
          </div>
          {/* vansh -- ends here */}

          <h1 className="text-2xl lg:text-3xl font-bold text-text-primary tracking-tight">Guest Information</h1>

          {/* Promo Code Section */}
          <div className="bg-white shadow-md rounded-xl p-4 lg:p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
              <div className="flex-shrink-0 flex items-center mb-3 sm:mb-0">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-brand-accent mr-2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                </svg>
                <span className="text-md font-semibold text-text-primary">Got a Promo Code?</span>
              </div>
              <div className="flex-grow flex items-center gap-3">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  disabled={state.hasDiscount || isApplyingPromo}
                  className="w-full flex-grow px-3 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/50 focus:border-brand-accent text-sm"
                  placeholder="Enter promo code"
                />
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  disabled={state.hasDiscount || isApplyingPromo || !promoCode.trim()}
                  className="bg-brand-accent text-text-dark-on-accent px-6 h-10 rounded-lg font-semibold text-sm hover:bg-brand-accent-hover transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isApplyingPromo ? '...' : state.hasDiscount ? 'Applied' : 'Apply'}
                </button>
              </div>
            </div>
            {state.hasDiscount && (
              <p className="text-sm text-green-600 mt-3 font-medium flex items-center gap-1">
                 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                </svg>
                Code <strong>{state.promoCode}</strong> applied! You saved {formatCurrency(state.discountAmount)}.
              </p>
            )}
          </div>

          {/* Booking Form */}
          <div className="bg-white shadow-md rounded-xl p-5 lg:p-6 border border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <label htmlFor="title" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Title *
                  </label>
                  <div className="relative">
                    <select
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      required
                      className="w-full px-3 h-11 bg-white border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    >
                      <option value="">Select Title</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Dr">Dr</option>
                      <option value="Prof">Prof</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                  </div>
                </div>
                
                {/* Spacer for desktop grid */}
                <div className="hidden md:block"></div>
                
                <div>
                  <label htmlFor="first-name" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first-name"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Jane"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label htmlFor="last-name" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last-name"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Doe"
                    autoComplete="family-name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="email" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    inputMode="email"
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="jane.doe@example.com"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                    inputMode="tel"
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="+91 98765 43210"
                    autoComplete="tel"
                  />
                </div>

                {/* vansh (this change) - Added Date of Birth Field */}
                <div>
                  <label htmlFor="dob" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dob"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                   
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                  />
                </div>
                {/* vansh -- ends here */}

                <div>
                  <label htmlFor="country" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Country *
                  </label>
                  <div className="relative">
                    <select
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      required
                      className="w-full px-3 h-11 bg-white border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                      autoComplete="country-name"
                    >
                      <option value="">Select Country</option>
                      <option value="India">India</option>
                      <option value="United States">United States</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                      <option value="Japan">Japan</option>
                      <option value="China">China</option>
                      <option value="Singapore">Singapore</option>
                      <option value="UAE">United Arab Emirates</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="city" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    required
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Mumbai"
                    autoComplete="address-level2"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    State
                  </label>
                  <input
                    type="text"
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}

                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Maharashtra"
                    autoComplete="address-level1"
                  />
                </div>
                <div>
                  <label htmlFor="pincode" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Pincode
                  </label>
                  <input
                    type="text"
                    id="pincode"
                    value={formData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                   
                    pattern="[0-9]{6}"
                    maxLength="6"
                    inputMode="numeric"
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="400001"
                    autoComplete="postal-code"
                  />
                </div>
                
                {/* vansh (this change) - Added GST Number Field (Optional) */}
                <div>
                  <label htmlFor="gstNumber" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    GST Number (Optional)
                  </label>
                  <input
                    type="text"
                    id="gstNumber"
                    value={formData.gstNumber}
                    onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Enter GSTIN"
                    maxLength="15"
                  />
                </div>
                {/* vansh -- ends here */}
                
              </div>

              <div>
                <label htmlFor="special-requests" className="block text-sm font-semibold text-text-primary mb-1">
                  Special Requests
                </label>
                <p className="text-xs text-text-secondary mb-2">(Optional)</p>
                <textarea
                  id="special-requests"
                  rows="4"
                  value={formData.specialRequests}
                  onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                  placeholder="e.g., High floor, early check-in, cot needed..."
                />
              </div>

              <div className="space-y-5 pt-2">
                {/* Terms and Conditions */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <h3 className="text-sm font-semibold text-text-primary mb-3">2. Terms and Condition</h3>
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="terms"
                        name="terms"
                        type="checkbox"
                        checked={formData.terms}
                        onChange={(e) => handleInputChange('terms', e.target.checked)}
                        required
                        className="h-5 w-5 text-brand-accent rounded border-gray-300 focus:ring-brand-accent cursor-pointer"
                      />
                    </div>
                    <label htmlFor="terms" className="ml-3 text-sm text-text-secondary cursor-pointer select-none">
                      Yes, I agree with WelcomHeritage <a href="#" className="font-medium text-brand-accent hover:underline">privacy policy</a>
                    </label>
                  </div>
                </div>

                {/* Cancellation Policy */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <h3 className="text-sm font-semibold text-text-primary mb-3">3. Cancellation Policy</h3>
                  <div className="flex items-start mb-3">
                    <div className="flex items-center h-5">
                      <input
                        id="cancellation"
                        name="cancellation"
                        type="checkbox"
                        checked={formData.cancellation}
                        onChange={(e) => handleInputChange('cancellation', e.target.checked)}
                        required
                        className="h-5 w-5 text-brand-accent rounded border-gray-300 focus:ring-brand-accent cursor-pointer"
                      />
                    </div>
                    <label htmlFor="cancellation" className="ml-3 text-sm text-text-secondary cursor-pointer select-none">
                      Yes, I agree with WelcomHeritage <a href="#" className="font-medium text-brand-accent hover:underline">cancellation policy</a>
                    </label>
                  </div>
                  <p className="text-xs text-text-secondary leading-relaxed pl-8">
                    By clicking on the button, I acknowledge and agree that above information is accurate and that my reservation will be subject to the Hotel Booking Terms and Policies.
                  </p>
                </div>
              </div>

              {/* Desktop Submit Button (Hidden on Mobile) */}
              <div className="hidden lg:block pt-4 border-t border-gray-200">
                {/* vansh (this change) - Added valid check to disable button */}
                <button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="w-full bg-brand-accent text-text-dark-on-accent px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-accent-hover transition-colors shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 active:translate-y-0 disabled:transform-none disabled:shadow-none"
                >
                  {isSubmitting ? 'Processing...' : 'Confirm & Pay'}
                </button>
                {/* vansh -- ends here */}
                <p className="text-xs text-text-secondary mt-3 text-center">
                  Your payment will be processed securely. You'll receive a confirmation email immediately.
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Booking Summary Sidebar (Desktop Only) */}
        <div className="hidden lg:block lg:w-1/3 w-full mt-8 lg:mt-0">
          <BookingSummary />
        </div>

      </div>

      {/* vansh (this change) - Mobile Sticky Bottom Bar with Validity Check */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 lg:hidden z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] safe-area-bottom">
        <div className="container mx-auto max-w-md flex gap-4 items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Total</span>
            <span className="text-xl font-bold text-text-dark-on-accent">
              {formatCurrency(state.hasDiscount ? state.finalTotal : state.total)}
            </span>
            <span className="text-[10px] text-gray-400">Include taxes</span>
          </div>
          <button 
            onClick={handleSubmit} 
            disabled={!isFormValid || isSubmitting}
            className="bg-brand-accent text-white px-8 py-3 rounded-xl font-bold text-base shadow-lg active:scale-95 transition-transform flex-grow max-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          >
            {isSubmitting ? '...' : 'Pay Now'}
          </button>
        </div>
      </div>
      {/* vansh -- ends here */}
    </div>
  );
};

// Booking Summary Component
const BookingSummary = () => {
  const { state, formatCurrency, formatDisplayDate, calculateNights } = useBooking();
  const nights = calculateNights();

  // 1. Calculate the actual total rooms from the cart
  const totalRoomsInCart = state.cart.reduce((acc, item) => acc + item.quantity, 0);

  // Group rooms by roomName
  const getGroupedRooms = () => {
    const groups = {};
    state.cart.forEach((item) => {
      const key = item.roomId;
      if (!groups[key]) {
        groups[key] = {
          ...item,
          totalAdults: (item.adultsCount || item.guestInfo?.adults || 1) * item.quantity,
          totalChildren: (item.childrenCount || item.guestInfo?.children || 0) * item.quantity,
          totalPrice: item.roomPrice * item.quantity * nights
        };
      } else {
        groups[key].quantity += item.quantity;
        groups[key].totalAdults += (item.adultsCount || item.guestInfo?.adults || 1) * item.quantity;
        groups[key].totalChildren += (item.childrenCount || item.guestInfo?.children || 0) * item.quantity;
        groups[key].totalPrice += (item.roomPrice * item.quantity * nights);
      }
    });
    return Object.values(groups);
  };

  const groupedRooms = getGroupedRooms();

  return (
    <div className="lg:sticky top-24">
      <div className="bg-white lg:shadow-md lg:rounded-xl lg:p-5 lg:border lg:border-gray-100">
        
       {/* Section 1: Stay Details */}
<div className="mb-4 pb-4 border-b border-gray-200">
  <h4 className="text-xs font-semibold text-text-secondary uppercase mb-3 tracking-wider">Stay Details</h4>
  <div className="flex justify-between items-start mb-3">
    <div>
      <p className="text-[10px] text-text-secondary uppercase font-bold tracking-tight">Check-in</p>
      <p className="text-sm font-bold text-text-primary">{formatDisplayDate(state.searchState.checkin)}</p>
      {/* Time Added Below */}
      <p className="text-[11px] text-text-secondary font-medium">From 02:00 PM</p>
    </div>
    
    <div className="h-10 border-l border-gray-200 mx-4 self-center"></div>
    
    <div className="text-right">
      <p className="text-[10px] text-text-secondary uppercase font-bold tracking-tight">Check-out</p>
      <p className="text-sm font-bold text-text-primary">{formatDisplayDate(state.searchState.checkout)}</p>
      {/* Time Added Below */}
      <p className="text-[11px] text-text-secondary font-medium">By 11:00 AM</p>
    </div>
  </div>
  
  {/* CALCULATED ROOMS, GUESTS, AND NIGHTS */}
  <div className="flex justify-between text-[11px] font-bold text-text-primary bg-gray-50 p-2.5 rounded-lg border border-gray-100">
    <div className="flex items-center gap-1">
      <span className="text-brand-primary">●</span>
      <span>{nights} {nights === 1 ? 'Night' : 'Nights'}</span>
    </div>
    <span>{totalRoomsInCart} Room(s)</span>
    <span>{state.searchState.guestCount} Guest(s)</span>
  </div>
</div>

        {/* Section 2: One-Row Room Breakup */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <h4 className="text-xs font-semibold text-text-secondary uppercase mb-3 tracking-wider">Selected Rooms</h4>
          <div className="space-y-3">
            {groupedRooms.map((group, index) => (
              <div key={index} className="flex justify-between items-center text-sm">
                <div className="text-text-primary">
                  <span className="font-bold">{group.quantity} x {group.roomName}</span>
                  <p className="text-[11px] text-text-secondary">
                    {group.totalAdults} Adult(s), {group.totalChildren} Child(ren)
                  </p>
                </div>
                <div className="font-bold text-text-primary">
                  {formatCurrency(group.totalPrice)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: Pricing */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className=" font-bold text-text-primary">Tariff for {nights} Night(s)</span>
            <span className="font-semibold text-text-primary">{formatCurrency(state.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Taxes</span>
            <span className="font-semibold text-text-primary">{formatCurrency(state.taxes)}</span>
          </div>
          <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-gray-100">
            <span className="text-base font-bold text-text-primary">Total Payable</span>
            <span className="text-xl font-bold text-text-primary">
              {formatCurrency(state.hasDiscount ? state.finalTotal : state.total)}
            </span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Booking;